// The /scenarios listing card's "near you" block (Trent, 2026-09-29): the same
// national band the scenario page draws, with the reader's local range shaded
// and the nearest hospital pinned, then the nearest and the cheapest within the
// radius and where each ranks among U.S. hospitals. Same numbers as the
// scenario page (useLocalScenarioPrices). Loads its scenario's coverage shard
// only once the card is near the viewport, so a long grid costs nothing until
// it is scrolled.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { PriceRangeBand } from "@/components/PriceRangeBand";
import NearbyQualityTable, { GradeBadge } from "@/components/scenarios/NearbyQualityTable";
import { titleCaseHospital } from "@/lib/hospitalName";
import { rankText, useLocalScenarioPrices, type LocalHospital } from "@/hooks/useLocalScenarioPrices";
import type { UserLocation } from "@/hooks/useUserLocation";
import { loadScenarioDetail } from "@/atlas/scenarioDetail";

interface Props {
  scenarioId: string;
  nationalTotal: number;
  band: { p10: number | null; median: number | null; avg: number | null; p90: number | null };
  location: UserLocation | null;
  radius: number;
  dark: boolean;
  /** The listing widens this card to the full grid while its table is open. */
  onTableToggle?: (open: boolean) => void;
}

const fmt = (v: number) => "$" + Math.round(v).toLocaleString("en-US");

/** CMS stars · would recommend · Google · ER time — what the scenario manifest carries per hospital. */
function qualityLine(l: LocalHospital): string {
  const q: string[] = [];
  if (l.info.cs) q.push(`${"★".repeat(Math.round(l.info.cs))}${"☆".repeat(5 - Math.round(l.info.cs))} CMS`);
  if (l.info.rp != null) q.push(`${Math.round(l.info.rp)}% would recommend`);
  if (l.info.gr != null) q.push(`${l.info.gr.toFixed(1)}★ Google${l.info.gn ? ` (${l.info.gn.toLocaleString("en-US")})` : ""}`);
  if (l.info.ew != null) q.push(`ER ${l.info.ew} min`);
  return q.join(" · ");
}

export default function ScenarioCardNearYou({ scenarioId, nationalTotal, band, location, radius, dark, onTableToggle }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const { loaded, local, nearest, cheapest, verdictOf, localRange } = useLocalScenarioPrices(scenarioId, nationalTotal, location, radius, visible);

  // Some callers can't supply the national spread (the search page's database
  // fallback, before the scenario index has loaded). Then read it from the
  // scenario's own detail shard — the figures /scenarios/<id> shows.
  const complete = (b: Props["band"]) => !!(b.p10 && b.p90 && b.median && b.p90 > b.p10);
  const [detailBand, setDetailBand] = useState<Props["band"] | null>(null);
  useEffect(() => {
    if (!visible || complete(band)) return;
    let dead = false;
    loadScenarioDetail(scenarioId).then((d) => {
      if (!dead && d) setDetailBand({ p10: d.p10Cost ?? null, median: d.estimatedCostMedian ?? null, avg: d.estimatedCost ?? null, p90: d.p90Cost ?? null });
    });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, scenarioId, band.p10, band.p90, band.median]);
  const B = complete(band) ? band : detailBand ?? band;

  const C = dark
    ? { text: "var(--civic-cream)", muted: "rgba(var(--civic-cream-rgb),0.72)", line: "rgba(var(--civic-cream-rgb),0.14)", good: "#6bc9a0", bad: "#ff7082", accent: "#7da6d4" }
    : { text: "var(--civic-ink)", muted: "rgba(var(--civic-ink-rgb),0.66)", line: "rgba(var(--civic-ink-rgb),0.12)", good: "#0F6B49", bad: "#9e1b2b", accent: "var(--civic-navy)" };

  const row = (label: string, l: LocalHospital, extra?: { text: string; color: string } | null) => (
    <div className="flex items-start gap-2 text-xs" data-near-line>
      <span className="mt-1 w-[4.6rem] flex-shrink-0 text-[10px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{label}</span>
      <GradeBadge g={l.letter} title={`Price grade ${l.letter} for this scenario`} />
      <span className="min-w-0 flex-1">
        <b className="tabular-nums" style={{ color: C.text }}>{fmt(l.price)}</b>{" "}
        <Link to={`/hospital/${l.id}`} className="hover:underline" style={{ color: C.text }} onClick={(e) => e.stopPropagation()}>{titleCaseHospital(l.info.n)}</Link>
        <span style={{ color: C.muted }}> · {Math.round(l.miles)} mi · </span>
        <span style={{ color: l.cheaperThanPct >= 50 ? C.good : C.bad, fontWeight: 600 }}>{rankText(l)}</span>
        {extra && <span style={{ color: extra.color, fontWeight: 600 }}> · {extra.text}</span>}
        {/* Quality in the preview, before the table is opened (Trent, 2026-09-29). */}
        <span className="block text-[11px]" style={{ color: C.muted }}>{qualityLine(l) || "No CMS or patient-survey ratings published"}</span>
      </span>
    </div>
  );

  // Band always (it is the scenario's national spread); the local layer only with a location.
  const bandEl = (
    <PriceRangeBand
      p10={B.p10 ?? undefined} median={B.median ?? undefined} avg={B.avg ?? undefined} p90={B.p90 ?? undefined}
      dark={dark} compact aa lesson={false} pinCaption={false}
      labels={{ p10: "CHEAPEST 10%", median: "MEDIAN", avg: "AVERAGE", p90: "PRICIEST 10%", pin: "NEAREST TO YOU" }}
      hospital={nearest ? { name: titleCaseHospital(nearest.info.n), price: nearest.price } : null}
      subject="for this scenario"
      localRange={localRange}
    />
  );

  const cheapVerdict = cheapest && nearest && cheapest.id !== nearest.id ? verdictOf(cheapest) : null;
  const verdict = cheapVerdict?.kind === "worth_it"
    ? { text: `save ${fmt(cheapVerdict.saving)} vs the nearest`, color: C.good }
    : null;

  return (
    <div ref={ref} className="mb-3" data-near-card>
      {bandEl}
      {location && visible && (
        <div className="mt-2 space-y-1 rounded-lg p-2" style={{ border: `1px solid ${C.line}` }}>
          <p className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: C.text }}>
            <MapPin className="h-3 w-3" style={{ color: C.accent }} />
            Near {location.label}
            <span className="font-normal normal-case tracking-normal" style={{ color: C.muted }}>
              {!loaded ? " · pricing…" : local.length ? ` · ${local.length} ${local.length === 1 ? "hospital" : "hospitals"} within ${radius} mi` : ` · none within ${radius} mi`}
            </span>
          </p>
          {loaded && nearest && row("Nearest", nearest)}
          {loaded && cheapest && cheapest.id !== nearest?.id && row("Cheapest", cheapest, verdict)}
          {loaded && local.length > 0 && (
            <button type="button" className="mt-1 text-xs font-semibold underline" style={{ color: C.accent }}
              aria-expanded={tableOpen} onClick={(e) => { e.stopPropagation(); setTableOpen((v) => { onTableToggle?.(!v); return !v; }); }}>
              {tableOpen ? "Hide" : `Compare all ${local.length} nearby hospitals — prices & quality`} {tableOpen ? "▴" : "▾"}
            </button>
          )}
        </div>
      )}
      {location && tableOpen && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <NearbyQualityTable
            rows={local} nearestId={nearest?.id ?? null} verdictOf={verdictOf} median={B.median} dark={dark} initial={8}
            caption={`Hospitals within ${radius} miles of ${location.label} that price this scenario: price, U.S. rank and quality`}
          />
        </div>
      )}
    </div>
  );
}
